import isEqual from 'lodash/isEqual';
import {
	type Action,
	createActionsHook,
	createContainer,
	createHook,
	createStateHook,
	createStore,
} from 'react-sweet-state';

import type { SelectFilterOption } from '../../../common/ui/filters/select-filter';
import type { QuickFindContent } from '../../quick-find/fetch-results/types';

export type SelectedQueryFilter = {
	filterId: string;
	keyword: string;
	displayText: string;
	option: SelectFilterOption;
};

type CurrentQueryFilter = {
	filterId: string;
	keyword: string;
};

export type QuickFindState = {
	quickFindOpen: boolean;
	quickFindQuery: string;
	content?: QuickFindContent;
	isFeedbackModalOpen: boolean;
	isBulkConnectModalOpen: boolean;
	fetchEntitiesRequestId?: string;
	fetchEntitiesTraceId?: string;
	fetchSpacesRequestId?: string;
	retryCount: number;
	updateRecentQueries?: () => void;
	updateZeroQueries?: () => void;
	selectedQueryFilters: SelectedQueryFilter[];
	currentQueryFilter?: CurrentQueryFilter;
	openHoverCardId: string | null;
};

export const initialState = {
	quickFindOpen: false,
	quickFindQuery: '',
	isFeedbackModalOpen: false,
	isBulkConnectModalOpen: false,
	retryCount: 0,
	updateRecentQueries: undefined,
	updateZeroQueries: undefined,
	selectedQueryFilters: [],
	openHoverCardId: null,
} satisfies QuickFindState;

const actions = {
	setQuickFindOpen:
		(quickFindOpen: QuickFindState['quickFindOpen']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ quickFindOpen });
		},
	setQuickFindQuery:
		(quickFindQuery: QuickFindState['quickFindQuery']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ quickFindQuery });
		},
	setContent:
		(content: QuickFindContent): Action<QuickFindState> =>
		({ setState }) => {
			setState({ content });
		},
	setIsBulkConnectModalOpen:
		(isBulkConnectModalOpen: QuickFindState['isBulkConnectModalOpen']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ isBulkConnectModalOpen });
		},
	setIsFeedbackModalOpen:
		(isFeedbackModalOpen: QuickFindState['isFeedbackModalOpen']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ isFeedbackModalOpen });
		},
	setRetryCount:
		(retryCount: QuickFindState['retryCount']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ retryCount });
		},
	setFetchSpacesRequestId:
		(fetchSpacesRequestId: QuickFindState['fetchSpacesRequestId']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ fetchSpacesRequestId });
		},
	setFetchEntitiesRequestId:
		(fetchEntitiesRequestId: QuickFindState['fetchEntitiesRequestId']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ fetchEntitiesRequestId });
		},
	setFetchEntitiesTraceId:
		(fetchEntitiesTraceId: QuickFindState['fetchEntitiesTraceId']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ fetchEntitiesTraceId });
		},
	setUpdateRecentQueries:
		(updateRecentQueries: QuickFindState['updateRecentQueries']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ updateRecentQueries });
		},
	setUpdateZeroQueries:
		(updateZeroQueries: QuickFindState['updateZeroQueries']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ updateZeroQueries });
		},
	addSelectedQueryFilter:
		(queryFilter: SelectedQueryFilter): Action<QuickFindState> =>
		({ setState, getState }) => {
			const { selectedQueryFilters } = getState();
			const duplicate = selectedQueryFilters.find((currentQueryFilter) =>
				isEqual(JSON.stringify(currentQueryFilter), JSON.stringify(queryFilter)),
			);

			if (!duplicate) {
				setState({ selectedQueryFilters: [...selectedQueryFilters, queryFilter] });
			}
		},
	deleteSelectedQueryFilter:
		(queryFilter: SelectedQueryFilter): Action<QuickFindState> =>
		({ setState, getState }) => {
			const { selectedQueryFilters } = getState();

			const newSelectedQueryFilters = selectedQueryFilters.filter(
				(currentQueryFilter) =>
					!isEqual(JSON.stringify(currentQueryFilter), JSON.stringify(queryFilter)),
			);

			setState({ selectedQueryFilters: newSelectedQueryFilters });
		},
	resetSelectedQueryFilters:
		(): Action<QuickFindState> =>
		({ setState }) => {
			setState({ selectedQueryFilters: [] });
		},
	setCurrentQueryFilter:
		(currentQueryFilter: QuickFindState['currentQueryFilter']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ currentQueryFilter });
		},
	setHoverCardOpen:
		(itemId: string, isOpen: boolean): Action<QuickFindState> =>
		({ setState, getState }) => {
			const { openHoverCardId } = getState();

			if (isOpen) {
				setState({ openHoverCardId: itemId });
			} else {
				if (openHoverCardId === itemId) {
					setState({ openHoverCardId: null });
				}
			}
		},
};

type TActions = typeof actions;

const Store = createStore<QuickFindState, TActions>({
	initialState,
	actions,
	name: 'quick-find',
});

export const useQuickFindActions = createActionsHook(Store);

export const useQuickFindOpen = createStateHook(Store, {
	selector: (state) => state.quickFindOpen,
});

export const useQuickFindQuery = createStateHook(Store, {
	selector: (state) => state.quickFindQuery,
});

export const useContent = createStateHook(Store, {
	selector: (state) => state.content,
});

export const useIsFeedbackModalOpen = createHook(Store, {
	selector: (state) => state.isFeedbackModalOpen,
});

export const useIsBulkConnectModalOpen = createHook(Store, {
	selector: (state) => state.isBulkConnectModalOpen,
});

export const useRetryCount = createStateHook(Store, { selector: (state) => state.retryCount });

export const useFetchSpacesRequestId = createStateHook(Store, {
	selector: (state) => state.fetchSpacesRequestId,
});

export const useFetchEntitiesRequestId = createStateHook(Store, {
	selector: (state) => state.fetchEntitiesRequestId,
});

export const useFetchEntitiesTraceId = createStateHook(Store, {
	selector: (state) => state.fetchEntitiesTraceId,
});

export const useUpdateRecentQueries = createStateHook(Store, {
	selector: (state) => state.updateRecentQueries,
});

export const useUpdateZeroQueries = createStateHook(Store, {
	selector: (state) => state.updateZeroQueries,
});

export const useSelectedQueryFilters = createStateHook(Store, {
	selector: (state) => state.selectedQueryFilters,
});

export const useCurrentQueryFilter = createStateHook(Store, {
	selector: (state) => state.currentQueryFilter,
});

export const useIsHoverCardOpen = createStateHook(Store, {
	selector: (state) => state.openHoverCardId !== null,
});

/**
 * This store is contained within a container as we may need to support multiple
 * instances of the Quick Find component in Nav3. This would be the case if we had
 * one in the navigation bar and another one in the search page.
 */
export const QuickFindStoreContainer = createContainer(Store);
